import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  HypermediaService,
  OrderableItem,
  OrderedItem,
  OrderedItemService,
  Order,
  SizePrice,
  OrderService,
  Upsell,
  UpsellWrapper,
  DeserializationUtils,
  AuditService,
  ConfigService,
  StoreConfig,
  Link,
} from 'ngx-web-api';
import { Observable, combineLatest, Subscription, noop, throwError, EMPTY } from 'rxjs';
import { map, mergeMap, filter, share, switchMap, distinctUntilChanged, take, catchError, tap } from 'rxjs/operators';
import { ItemEditorService } from '../../core/services/item-editor.service';
import { ThemeService } from '../../core/services/theme.service';
import { Theme } from '../../domain/theme.model';
import { MenuWrapperService } from '../../core/services/menu-wrapper.service';
import { EditorComponent } from '../editor/editor.component';
import { ModalService } from '../../core/services/modal.service';
import { HttpClient } from '@angular/common/http';
import { ViewportService } from 'app/core/services/viewport.service';
import { ViewPort } from 'app/domain/view-port.model';
import { OrderUpsellService } from 'app/core/services/order-upsell.service';

@Component({
  selector: 'fts-ordered-editor',
  templateUrl: './ordered-editor.component.html',
  styleUrls: ['./ordered-editor.component.scss'],
})
export class OrderedEditorComponent implements OnInit, OnDestroy {
  @Input()
  inItemEditorModal = false;
  @Input()
  itemOrdinalId: number;

  @Output()
  finishLoading = new EventEmitter<boolean>();

  private subscriptions: Subscription[];
  @ViewChild(EditorComponent, { static: false })
  editor: EditorComponent;

  item: OrderedItem;
  orderableItem: OrderableItem;
  upsellItems: Upsell[];
  theme: Theme;
  selectedSizePrice: SizePrice;
  loading = true;
  storeConfig: StoreConfig;
  viewport$: Observable<ViewPort>;

  constructor(
    private route: ActivatedRoute,
    private menuService: MenuWrapperService,
    private orderService: OrderService,
    private themeService: ThemeService,
    private hypermedia: HypermediaService,
    private router: Router,
    private http: HttpClient,
    private auditService: AuditService,
    private itemEditorService: ItemEditorService,
    private orderedItemService: OrderedItemService,
    private modalService: ModalService,
    private configService: ConfigService,
    public viewportService: ViewportService,
    private orderUpsellService: OrderUpsellService
  ) {}

  ngOnInit() {
    let orderedItem$: Observable<OrderedItem>;
    if (this.inItemEditorModal) {
      orderedItem$ = this.getOrderedItem(this.itemOrdinalId).pipe(catchError(error => throwError(error)));
    } else {
      orderedItem$ = this.route.params.pipe(
        map(params => +params['item-ordinal']),
        distinctUntilChanged(),
        switchMap((itemId: number) => this.getOrderedItem(itemId)),
        filter(orderedItem => !!orderedItem)
      );
    }
    const orderableItemLink$: Observable<string> = orderedItem$.pipe(
      map((orderedItem: OrderedItem) => this.hypermedia.findLink(orderedItem.links, 'orderableItem')),
      filter(link => !!link),
      map((link: Link) => link.href)
    );

    this.subscriptions = [
      this.initEditingItem(orderedItem$, orderableItemLink$),
      this.itemEditorService.editingOrderableItem$
        .pipe(filter((orderable: OrderableItem) => !!orderable))
        .subscribe(orderable => (this.orderableItem = orderable.copy())),
      this.redirectWhenItemGetsDeleted(),
      this.themeService.theme.subscribe(theme => (this.theme = theme)),
      this.itemEditorService.editingItemSelectedSizePrice$.pipe(filter(sizePrice => !!sizePrice)).subscribe((sizePrice: SizePrice) => {
        this.selectedSizePrice = sizePrice;
        if (this.item) {
          this.item.size = sizePrice.size;
        }
      }),
      this.configService.storeConfig$.pipe(take(1)).subscribe(
        config => (this.storeConfig = config),
        () => noop
      ),
    ];
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s: Subscription) => s.unsubscribe());
  }

  public canLeaveEditor(): boolean {
    if (!!this.editor) {
      return this.editor.canLeaveEditor();
    }

    return true;
  }

  public onChangeSizeTriggerUpsells({ orderableItem, orderedItem }: { orderableItem: OrderableItem; orderedItem: OrderedItem }) {
    this.getUpsellItems(orderableItem, orderedItem).subscribe(upsellItems => (this.upsellItems = upsellItems));
  }

  private getUpsellItems(item: OrderableItem, orderedItem: OrderedItem): Observable<Upsell[]> {
    return this.orderUpsellService.getUpsells(item, orderedItem).pipe(
      map((json: Object[]): UpsellWrapper[] => DeserializationUtils.deserializeArray(json, UpsellWrapper)),
      map((upsellWrappers: UpsellWrapper[]) => upsellWrappers.filter(upsellWrapper => !!upsellWrapper.items)),
      map((upsellWrappers: UpsellWrapper[]) => {
        if (upsellWrappers && upsellWrappers.length > 0) {
          return upsellWrappers[0].items;
        }
      })
    );
  }

  private getOrderedItem(itemId: number): Observable<OrderedItem> {
    return this.orderService.order$.pipe(
      map((order: Order) => order.allItems.find(item => item.itemId === itemId)),
      take(1)
    );
  }

  private initEditingItem(orderedItem$: Observable<OrderedItem>, orderableItemLink$: Observable<string>): Subscription {
    const orderableItem$: Observable<OrderableItem> = orderableItemLink$.pipe(
      mergeMap((link: string) => this.http.get(link)),
      map(json => new OrderableItem().deserialize(json)),
      share()
    );

    return combineLatest([
      orderedItem$,
      orderableItem$,
      orderableItem$.pipe(
        mergeMap((i: OrderableItem) => orderedItem$.pipe(mergeMap(orderedItem => this.getUpsellItems(i, orderedItem)))),
        catchError(err => {
          if (err.status === 400) {
            return this.modalService.openErrorNotificationModal(err.error.meta.info).pipe(
              tap(() => {
                if (this.theme.showItemEditorModal) {
                  this.modalService.closeAllModals();
                  return;
                }
                this.router.navigate(['/menu']);
              }),
              catchError(err => {
                if (this.theme.showItemEditorModal) {
                  this.modalService.closeAllModals();
                  return;
                }
                this.router.navigate(['/menu']);
                return throwError(EMPTY);
              })
            );
          }
        })
      ),
    ]).subscribe(([orderedItem, orderableItem, upsellItems]: [OrderedItem, OrderableItem, Upsell[]]) => {
      if (!this.item || this.item.itemId !== orderedItem.itemId) {
        this.auditService.createAudit(() => `Editing item '${orderedItem.size} ${orderedItem.item}' with id '${orderedItem.itemId}'`);
      }
      this.item = orderedItem.copy();
      this.upsellItems = upsellItems;
      this.orderableItem = orderableItem;
      this.itemEditorService.updateEditingItem(orderableItem, this.item);
      this.loading = false;
      this.finishLoading.emit(true);
    }, noop);
  }

  private redirectWhenItemGetsDeleted(): Subscription {
    return this.orderedItemService.deletedItem$
      .pipe(
        filter((item: OrderedItem) => !this.item || this.item.itemId === item.itemId),
        mergeMap(() => this.orderService.fetchOrder()),
        mergeMap(() => this.menuService.getFirstPanelItemName()),
        take(1)
      )
      .subscribe(firstPanelName =>
        this.router.navigate(['/menu', firstPanelName], {
          queryParams: { redirectFromDelete: true },
        })
      );
  }
}
